'use client'

import { useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'

import type { JobItem, PaginatedResponse } from '@types'

import useHttpRequest from '@hooks/useHttpRequest'
import useLocale from '@hooks/client/useLocale'
import useDateTime from '@hooks/client/useDateTime'
import Pagination from '@components/pagination/Pagination'

import companyPlaceholderIcon from '@images/searchresults/company.png'

import styles from '../jobs.module.scss'
import { jobUrlBuilder } from '@lib/helpers/client'

type JobsResultsProps = {
  onDataChange?: (data: { totalElements: number; currentCount: number; isLoading: boolean }) => void
}

export default function JobsResults({ onDataChange }: JobsResultsProps): JSX.Element {
  const searchParams = useSearchParams()
  const { get } = useHttpRequest()
  const locale = useLocale()
  const dateTime = useDateTime()
  const [jobs, setJobs] = useState<JobItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [totalElements, setTotalElements] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(0)

  // Create a stable string representation of search params to use as dependency
  // Include query and location_name to ensure we detect changes
  const searchParamsString = useMemo(() => {
    const query = searchParams.get('query')
    const locationName = searchParams.get('location_name')
    const locationId = searchParams.get('location_id')
    return `${query || ''}-${locationName || ''}-${locationId || ''}-${searchParams.toString()}`
  }, [searchParams])

  useEffect(() => {
    const fetchJobs = async (): Promise<void> => {
      const query = searchParams.get('query')
      const locationName = searchParams.get('location_name')
      const locationId = searchParams.get('location_id')

      // Require at least a location to search (query can be empty when navigating through category/state/county)
      if (!locationName && !locationId) {
        setIsLoading(false)
        setJobs([])
        if (onDataChange) {
          onDataChange({ totalElements: 0, currentCount: 0, isLoading: false })
        }
        return
      }

      setIsLoading(true)
      if (onDataChange) {
        onDataChange({ totalElements: 0, currentCount: 0, isLoading: true })
      }

      // Build search params object
      const searchParamsObj: Record<string, string | number | boolean> = {
        location_name: locationName || '',
        page: Number(searchParams.get('page')) || 1,
      }

      // Only add query if it's not empty
      if (query && query.trim()) {
        searchParamsObj.query = query.trim()
      }

      if (locationId) {
        searchParamsObj.location_id = locationId
      }

      // Add filter params - use defaults if not present in URL (matching /us/jobs behavior)
      const maxRadius = searchParams.get('max_radius')
      searchParamsObj.max_radius = maxRadius ? Number(maxRadius) : 40.234 // Default 25 miles

      const createdAfter = searchParams.get('created_after')
      if (createdAfter) {
        searchParamsObj.created_after = createdAfter
      }

      // Default to true for full_time and part_time if not specified (matching /us/jobs behavior)
      const fullTime = searchParams.get('full_time')
      searchParamsObj.full_time = fullTime !== 'false' // Default to true

      const partTime = searchParams.get('part_time')
      searchParamsObj.part_time = partTime !== 'false' // Default to true

      const remote = searchParams.get('remote')
      searchParamsObj.remote = remote === 'true' // Default to false

      const noExperience = searchParams.get('no_experience')
      searchParamsObj.no_experience = noExperience === 'true' // Default to false

      const salaryMin = searchParams.get('salary_min')
      if (salaryMin) {
        searchParamsObj.salary_min = Number(salaryMin)
      }

      const salaryType = searchParams.get('salary_type')
      if (salaryType) {
        searchParamsObj.salary_type = salaryType
      }

      const sort = searchParams.get('sort')
      searchParamsObj.sort = sort || 'FEATURED' // Default to FEATURED if not specified

      const macro = searchParams.get('macro')
      if (macro) {
        searchParamsObj.macro = macro
      }

      const locationState = searchParams.get('location_state')
      if (locationState) {
        searchParamsObj.location_state = locationState
      }

      const locationCounty = searchParams.get('location_county')
      if (locationCounty) {
        searchParamsObj.location_county = locationCounty
      }

      const queryString = new URLSearchParams(
        Object.entries(searchParamsObj).reduce(
          (acc, [key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              acc[key] = String(value)
            }
            return acc
          },
          {} as Record<string, string>,
        ),
      ).toString()

      // The API route will add agent=USER&hq_only=false via SearchService.searchJobs
      // This matches the behavior of /us/search
      try {
        const response = await get<PaginatedResponse<JobItem[]>>(`jobs/fetch-jobs?${queryString}`, { service: 'web' })

        if (response) {
          const jobsData = response.content || []
          const total = response.totalElements || 0
          setJobs(jobsData)
          setTotalElements(total)
          setTotalPages(response.totalPages || 0)
          // Notify parent component of data change
          if (onDataChange) {
            onDataChange({ totalElements: total, currentCount: jobsData.length, isLoading: false })
          }
        } else {
          setJobs([])
          setTotalElements(0)
          setTotalPages(0)
          if (onDataChange) {
            onDataChange({ totalElements: 0, currentCount: 0, isLoading: false })
          }
        }
      } catch (error) {
        console.error('Error fetching jobs:', error)
        setJobs([])
        setTotalElements(0)
        setTotalPages(0)
        if (onDataChange) {
          onDataChange({ totalElements: 0, currentCount: 0, isLoading: false })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString])

  const locationName = searchParams.get('location_name')
  const locationId = searchParams.get('location_id')

  // Don't render anything if no location has been provided
  if (!locationName && !locationId) {
    return <></>
  }

  if (isLoading) {
    return (
      <div className={styles.jobsList}>
        <div className={styles.jobsCard}>
          <div className={styles.jobsCardBody}>Loading jobs...</div>
        </div>
      </div>
    )
  }

  if (jobs.length === 0) {
    return (
      <div className={styles.jobsList}>
        <div className={styles.jobsCard}>
          <div className={styles.jobsCardBody}>No jobs found. Try adjusting your search criteria.</div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.jobsList}>
      {jobs.map((job: JobItem) => {
        const salary = job.salaryDescription || job.externalJobDetail?.salaryDescription || 'Competitive'
        const title = job.title || 'Missing data'
        const company = job.companyName || 'Missing data'
        const description =
          job.jobDescription?.replace(/<[^>]+>/g, '').substring(0, 200) ||
          job.externalJobDetail?.description?.replace(/<[^>]+>/g, '').substring(0, 200) ||
          'Missing data'
        const benefits = job.benefits && job.benefits.length > 0 ? job.benefits : []
        const city = job.address?.city || 'Missing data'
        const dateLabel = job.publishDateTime ? dateTime(job.publishDateTime).fromNow() : 'Missing data'
        const companyLogo = job.companyJobLogoURL || job.featuredImageUrl || companyPlaceholderIcon

        const jobUrl = jobUrlBuilder(job, locale)
        return (
          <a className={styles.jobsCard} href={jobUrl} key={job.id}>
            <div className={styles.jobsCardContent}>
              <div className={styles.jobsCardIcon}>
                <Image src={companyLogo} alt={company} width={56} height={56} style={{ objectFit: 'contain' }} />
              </div>
              <div className={styles.jobsCardBody}>
                <div className={styles.jobsCardHeader}>
                  <h3 className={styles.jobsCardTitle}>{title}</h3>
                </div>
                <div className={styles.jobsCardSalary}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" x2="12" y1="2" y2="22"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  {salary}
                </div>
                <p className={styles.jobsCardCompany}>{company}</p>
                <p className={styles.jobsCardDescription}>{description}...</p>
                {benefits.length > 0 && (
                  <div className={styles.jobsCardTags}>
                    {benefits.slice(0, 4).map((benefit, idx) => (
                      <span className={styles.jobsCardTag} key={idx}>
                        {benefit}
                      </span>
                    ))}
                  </div>
                )}
                <div className={styles.jobsCardMeta}>
                  <span className={styles.jobsCardMetaItem}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    {city}
                  </span>
                  <span className={styles.jobsCardMetaItem}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    {dateLabel}
                  </span>
                </div>
              </div>
            </div>
          </a>
        )
      })}
      {totalPages > 1 && (
        <div className={styles.jobsPagination}>
          <Pagination totalPages={totalPages} />
        </div>
      )}
      {totalElements > 0 && (
        <div className={styles.jobsCount}>
          Showing {jobs.length} of {totalElements} jobs
        </div>
      )}
    </div>
  )
}
